import React, { useState } from "react";
import {
  ArrowRight,
  User,
  Phone,
  Mail,
  MessageSquare,
  UploadCloud,
} from "lucide-react";
import { validateField, validateForm } from "../utils/pqrsValidation";

import Swal from "sweetalert2";
import { sendPQRS } from "../services/pqrsService";

const PQRS = () => {
  const [formData, setFormData] = useState({
    name: "",
    phone: "",
    email: "",
    type: "",
    message: "",
    file: null,
  });

  const [errors, setErrors] = useState({});

  const [touched, setTouched] = useState({
    name: false,
    phone: false,
    email: false,
    type: false,
    message: false,
  });

  const handleChange = (e) => {
    const { name, value } = e.target;

    let newValue = value;

    if (name === "phone") {
      newValue = value.replace(/\D/g, "").slice(0, 15);
    }

    setFormData((prev) => ({
      ...prev,
      [name]: newValue,
    }));

    setTouched((prev) => ({
      ...prev,
      [name]: true,
    }));

    // Validación en tiempo real
    if (newValue.trim().length > 0) {
      setErrors((prev) => ({
        ...prev,
        [name]: validateField(name, newValue),
      }));
    } else {
      setErrors((prev) => ({
        ...prev,
        [name]: "",
      }));
    }
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];

    setFormData((prev) => ({
      ...prev,
      file,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    setTouched({
      name: true,
      phone: true,
      email: true,
      type: true,
      message: true,
    });

    const newErrors = validateForm(formData);

    setErrors(newErrors);

    if (Object.keys(newErrors).length > 0) {
      return;
    }

    try {
      Swal.fire({
        title: "Enviando PQRS",
        text: "Por favor espera un momento...",
        allowOutsideClick: false,
        allowEscapeKey: false,
        didOpen: () => {
          Swal.showLoading();
        },
      });

      await sendPQRS(formData);

      Swal.fire({
        icon: "success",
        title: "¡PQRS enviada!",
        text: "Tu solicitud fue enviada correctamente. Nuestro equipo la revisará y te dará respuesta lo antes posible.",
        confirmButtonText: "Aceptar",
        confirmButtonColor: "#ee7d32",
      });

      setFormData({
        name: "",
        phone: "",
        email: "",
        type: "",
        message: "",
        file: null,
      });

      setErrors({});

      setTouched({
        name: false,
        phone: false,
        email: false,
        type: false,
        message: false,
      });

      const fileInput = document.querySelector('input[type="file"]');

      if (fileInput) {
        fileInput.value = "";
      }
    } catch (error) {
      console.error("Error al enviar PQRS:", error);

      Swal.fire({
        icon: "error",
        title: "No se pudo enviar",
        text:
          error.message ||
          "Ocurrió un error al enviar tu solicitud. Por favor, inténtalo nuevamente.",
        confirmButtonText: "Aceptar",
        confirmButtonColor: "#ee7d32",
      });
    }
  };
  return (
    <div className="min-h-screen bg-gradient-to-br from-orange-50 via-white to-blue-50 flex items-center justify-center px-4 py-10">
      <div className="w-full max-w-6xl grid md:grid-cols-2 bg-white rounded-3xl shadow-2xl overflow-hidden">
        {/* PANEL VISUAL */}
        <div className="relative overflow-hidden min-h-[500px]">
          <img
            src="/Images/pqrs/pqrs.webp"
            alt="Sierra Alta"
            className="absolute inset-0 w-full h-full object-cover"
          />

          <div className="absolute inset-0 bg-gradient-to-r from-black/80 via-black/60 to-black/30" />

          <div className="absolute inset-0 bg-black/20" />

          <div className="absolute top-0 right-0 w-72 h-72 bg-[#ee7d32]/20 rounded-full blur-[120px]" />

          <div className="relative z-10 h-full p-8 lg:p-12 flex flex-col justify-center">
            <div className="max-w-md">
              <span className="inline-block text-base md:text-lg tracking-widest text-[#ee7d32] uppercase font-semibold bg-white/10 px-3 py-1 rounded-full">
                PQRS
              </span>

              <h3 className="mt-4 text-4xl lg:text-5xl font-bold text-white leading-tight drop-shadow-lg">
                Estamos para escucharte
              </h3>

              <p className="mt-5 text-white/90 text-lg leading-relaxed drop-shadow-md">
                Envíanos tus peticiones, quejas, reclamos o sugerencias. Nuestro
                equipo te dará respuesta en el menor tiempo posible.
              </p>
            </div>
          </div>
        </div>

        {/* FORMULARIO */}
        <div className="relative p-8 lg:p-14">
          <div className="absolute top-0 right-0 w-72 h-72 bg-[#ee7d32]/5 rounded-full blur-[120px]" />

          <div className="relative z-10 max-w-4xl">
            <h3 className="text-3xl font-bold text-gray-900">Enviar PQRS</h3>

            <p className="mt-3 text-gray-600">
              Completa el formulario y gestionaremos tu solicitud lo antes
              posible.
            </p>

            <form onSubmit={handleSubmit} className="mt-10 space-y-5">
              {/* NOMBRE */}
              <div>
                <div className="relative">
                  <User
                    size={18}
                    className="absolute left-5 top-1/2 -translate-y-1/2 text-gray-400"
                  />

                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    placeholder="Nombre completo"
                    autoComplete="off"
                    className={`border border-solid
          w-full pl-14 pr-6 py-5 bg-white rounded-3xl shadow-sm
          outline-none transition-all
          ${
            touched.name && errors.name
              ? "border border-red-400"
              : "border border-gray-100"
          }
          focus:border-[#ee7d32]
          focus:ring-2 focus:ring-[#ee7d32]/10
        `}
                  />
                </div>

                {touched.name && errors.name && (
                  <p className="mt-2 text-sm text-red-500">{errors.name}</p>
                )}
              </div>

              {/* TELÉFONO */}
              <div>
                <div className="relative">
                  <Phone
                    size={18}
                    className="absolute left-5 top-1/2 -translate-y-1/2 text-gray-400"
                  />

                  <input
                    type="text"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    placeholder="Teléfono"
                    autoComplete="off"
                    className={`border border-solid
          w-full pl-14 pr-6 py-5 bg-white rounded-3xl shadow-sm
          outline-none transition-all
          ${
            touched.phone && errors.phone
              ? "border border-red-400"
              : "border border-gray-100"
          }
          focus:border-[#ee7d32]
          focus:ring-2 focus:ring-[#ee7d32]/10
        `}
                  />
                </div>

                {touched.phone && errors.phone && (
                  <p className="mt-2 text-sm text-red-500">{errors.phone}</p>
                )}
              </div>

              {/* CORREO */}
              <div>
                <div className="relative">
                  <Mail
                    size={18}
                    className="absolute left-5 top-1/2 -translate-y-1/2 text-gray-400"
                  />

                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    placeholder="Correo electrónico"
                    autoComplete="off"
                    className={`border border-solid
          w-full pl-14 pr-6 py-5 bg-white rounded-3xl shadow-sm
          outline-none transition-all
          ${
            touched.email && errors.email
              ? "border border-red-400"
              : "border border-gray-100"
          }
          focus:border-[#ee7d32]
          focus:ring-2 focus:ring-[#ee7d32]/10
        `}
                  />
                </div>

                {touched.email && errors.email && (
                  <p className="mt-2 text-sm text-red-500">{errors.email}</p>
                )}
              </div>

              {/* TIPO PQRS */}
              <div>
                <div className="relative">
                  <select
                    name="type"
                    value={formData.type}
                    onChange={handleChange}
                    className={`border border-solid
        w-full px-6 pr-12 py-5
        bg-white
        rounded-3xl
        shadow-sm
        outline-none
        appearance-none
        transition-all
        ${
          touched.type && errors.type
            ? "border border-red-400"
            : "border border-gray-100"
        }
        focus:border-[#ee7d32]
        focus:ring-2 focus:ring-[#ee7d32]/10
      `}
                  >
                    <option value="">Tipo de solicitud</option>
                    <option value="peticion">Petición</option>
                    <option value="queja">Queja</option>
                    <option value="reclamo">Reclamo</option>
                    <option value="sugerencia">Sugerencia</option>
                  </select>

                  <svg
                    className="absolute right-5 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400 pointer-events-none"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M19 9l-7 7-7-7"
                    />
                  </svg>
                </div>

                {touched.type && errors.type && (
                  <p className="mt-2 text-sm text-red-500">{errors.type}</p>
                )}
              </div>

              {/* MENSAJE */}
              <div>
                <div className="relative">
                  <MessageSquare
                    size={18}
                    className="absolute left-5 top-6 text-gray-400"
                  />

                  <textarea
                    rows={5}
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    placeholder="Describe tu solicitud..."
                    className={`border border-solid
          w-full pl-14 pr-6 py-5 bg-white rounded-3xl shadow-sm
          resize-none outline-none transition-all
          ${
            touched.message && errors.message
              ? "border border-red-400"
              : "border border-gray-100"
          }
          focus:border-[#ee7d32]
          focus:ring-2 focus:ring-[#ee7d32]/10
        `}
                  />
                </div>

                {touched.message && errors.message && (
                  <p className="mt-2 text-sm text-red-500">{errors.message}</p>
                )}
              </div>

              {/* ARCHIVOS */}
              <label
                className="
      group
      flex flex-col items-center justify-center gap-4
      w-full p-8
      rounded-3xl
      border-2 border-dashed border-gray-300
      bg-white
      cursor-pointer
      transition-all duration-300
      hover:border-[#ee7d32]
      hover:bg-[#fff7f2]
    "
              >
                <div
                  className="
        w-14 h-14 rounded-2xl
        bg-orange-100
        flex items-center justify-center
        transition-all duration-300
        group-hover:scale-110
      "
                >
                  <UploadCloud size={26} className="text-[#ee7d32]" />
                </div>

                <div className="text-center">
                  <p className="font-medium text-gray-700">
                    Adjunta archivos de soporte
                  </p>

                  <p className="text-sm text-gray-500 mt-1">
                    PDF, DOCX, JPG o PNG · Máx. 5MB
                  </p>
                </div>

                <span className="text-sm font-medium text-[#ee7d32]">
                  Seleccionar archivo
                </span>

                {formData.file && (
                  <span className="text-sm font-medium text-gray-700">
                    {formData.file.name}
                  </span>
                )}

                <input
                  type="file"
                  className="hidden"
                  accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
                  onChange={handleFileChange}
                />
              </label>

              {/* BOTÓN */}
              <button
                type="submit"
                className="
      group
      w-full
      flex items-center justify-center gap-3
      px-8 py-5
      rounded-3xl
      bg-gradient-to-r from-[#ee7d32] to-[#ff9d58]
      text-white font-semibold
      shadow-[0_20px_40px_rgba(238,125,50,.25)]
      hover:shadow-[0_25px_50px_rgba(238,125,50,.35)]
      hover:scale-[1.01]
      transition-all duration-300
    "
              >
                Enviar PQRS
                <ArrowRight
                  size={18}
                  stroke="white"
                  className="transition-transform duration-300 group-hover:translate-x-2"
                />
              </button>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};
export default PQRS;
